import { Parcel } from '~api/som';

import { Fragment, forwardRef } from 'react';
import { useFieldArray } from 'react-hook-form';

import { Add16 } from '@ingrid/iconography/react';
import { Button, Card, Link, ListLayout } from '@ingrid/luterkort-react';
import { ButtonVariantProps } from '@ingrid/luterkort-react/dist/Button';

import { ClearConfirmationModal } from '~src/components/ClearConfirmationModal/ClearConfirmationModal';
import { Dimensions } from '~src/components/Dimensions/Dimensions';
import { Weight } from '~src/components/Weight/Weight';
import { useTranslation } from '~src/i18n/use-translation';
import { Format } from '~src/utils/format';

import { useShipmentFormContext } from '../types';
import { LineItemFormDialog } from './line-item-form-dialog';
import { ParcelAddSchema, ParcelFormDialog } from './parcel-form-dialog';

const AddParcelButton = forwardRef<HTMLButtonElement, ButtonVariantProps>(function AddParcelButton(props, ref) {
  const { t } = useTranslation();

  return (
    <Button type="button" variant="secondary" ref={ref} {...props}>
      <Add16 />
      {t('common.addParcel')}
    </Button>
  );
});

interface ParcelCardProps {
  parcel: Parcel;
  index: number;
  onClear: () => void;
  onEdit: (payload: ParcelAddSchema) => void;
}

function ParcelCard({ parcel, index, onClear, onEdit }: ParcelCardProps) {
  const { control, getValues } = useShipmentFormContext();
  const { t } = useTranslation();

  const { fields, append, remove, update } = useFieldArray({
    name: `parcels.${index}.contents.goods`,
    control,
  });

  return (
    <Card.Content>
      <ListLayout.Column>
        <ListLayout.Column>
          <ListLayout.Stack>
            <ListLayout.SubRow>
              <ListLayout.PrimaryText>
                {parcel.free_text?.length ? parcel.free_text : t('common.parcel')}
              </ListLayout.PrimaryText>

              <ListLayout.Actions>
                <ClearConfirmationModal clearedItemName="common.parcel" onSubmit={onClear} />

                <ParcelFormDialog
                  onSubmit={payload =>
                    onEdit({
                      ...getValues(`parcels.${index}`),
                      ...payload,
                    })
                  }
                  trigger={<Link as="button">{t('common.edit')}</Link>}
                  defaultValues={parcel}
                  mode="edit"
                />
              </ListLayout.Actions>
            </ListLayout.SubRow>

            <ListLayout.SubRow gap="lg">
              <ListLayout.SecondaryText>
                {fields.length} {t(fields.length === 1 ? 'common.item' : 'common.items')}
              </ListLayout.SecondaryText>

              {parcel.weight !== undefined && (
                <ListLayout.SecondaryText>
                  <Weight weight={parcel.weight} />
                </ListLayout.SecondaryText>
              )}

              {parcel.dimensions !== undefined && (
                <ListLayout.SecondaryText>
                  <Dimensions dimensions={parcel.dimensions} />
                </ListLayout.SecondaryText>
              )}
            </ListLayout.SubRow>
          </ListLayout.Stack>

          {fields.length > 0 &&
            fields.map((item, index) => (
              <ListLayout.Row style={{ marginLeft: '16px' }} key={item.id}>
                <ListLayout.Column style={{ width: '100%' }}>
                  <ListLayout.Stack>
                    <ListLayout.SubRow>
                      <ListLayout.PrimaryText>
                        {item.quantity}&times; {item.name}
                      </ListLayout.PrimaryText>

                      <ListLayout.Actions>
                        <ClearConfirmationModal clearedItemName="common.lineItem" onSubmit={() => remove(index)} />

                        <LineItemFormDialog
                          trigger={<Link as="button">{t('common.edit')}</Link>}
                          onSubmit={p => update(index, p)}
                          defaultValues={item}
                          mode="edit"
                        />
                      </ListLayout.Actions>
                    </ListLayout.SubRow>

                    <ListLayout.SubRow gap="lg">
                      <ListLayout.SecondaryText>{Format.Id(item.sku ?? '')}</ListLayout.SecondaryText>

                      {item.weight !== undefined && (
                        <ListLayout.SecondaryText>
                          <Weight weight={item.weight} />
                        </ListLayout.SecondaryText>
                      )}

                      {item.dimensions !== undefined && (
                        <ListLayout.SecondaryText>
                          <Dimensions dimensions={item.dimensions} />
                        </ListLayout.SecondaryText>
                      )}
                    </ListLayout.SubRow>
                  </ListLayout.Stack>
                </ListLayout.Column>
              </ListLayout.Row>
            ))}
        </ListLayout.Column>

        <LineItemFormDialog
          onSubmit={li => append(li)}
          mode="add"
          trigger={
            <Link style={{ marginLeft: '16px' }} as="button">
              <Add16 /> {t('common.addLineItem')}
            </Link>
          }
        />
      </ListLayout.Column>
    </Card.Content>
  );
}

function Editor() {
  const { control } = useShipmentFormContext();
  const { fields, append, remove, update } = useFieldArray({ name: 'parcels', control });

  if (!fields.length) {
    return (
      <Card.Content>
        <ParcelFormDialog trigger={<AddParcelButton />} onSubmit={p => append(p)} mode="add" />
      </Card.Content>
    );
  }

  return (
    <Fragment>
      {fields.map((parcel, index) => (
        <ParcelCard
          onEdit={p => update(index, p)}
          onClear={() => remove(index)}
          parcel={parcel}
          key={parcel.id}
          index={index}
        />
      ))}

      <Card.Footer>
        <ParcelFormDialog
          trigger={fields.length < 50 ? <AddParcelButton size="sm" /> : undefined}
          onSubmit={p => append(p)}
          mode="add"
        />
      </Card.Footer>
    </Fragment>
  );
}

export function Consignment() {
  const { t } = useTranslation();

  return (
    <Card.Wrapper as="section">
      <Card.Header>{t('shipments.consignment.details')}</Card.Header>
      <Editor />
    </Card.Wrapper>
  );
}
